<template>
  <div class="dropdown-wrapper">
    <span class="dropdown-selector-text">{{ $t('generation.image_style') }}:</span>
    <dropdown-menu class="style-preset-selector" transition="zoom" ref="dropdown" @opened="scrollIntoViewIfNeeded">
      <template #trigger>
        <button class="style-preset-selector-button" :disabled="$store.getters.generationInProgress || $store.getters.generationDisabledGlobally">
          <span v-if="selectedEffectGenerationStylePreset">{{ $t(`generation.styles.${convertStyleKey(selectedEffectGenerationStylePreset.style)}`) }}</span>
          <span v-else>{{ $t('generation.select_style') }}</span>
        </button>
      </template>
      <template #header>{{ $t('generation.select_style') }}</template>
      <template #body>
        <ul>
          <li v-for="preset in availableStylePresets" :key="preset.ID">
            <div :class="{ 'selected': $store.getters.selectedEffectGenerationStylePresetID === preset.ID }">
              <div class="preset-image-wrapper">
                <img :src="preset.imageURL" :alt="preset.style"/>
              </div>
              <div class="preset-text-wrapper" @click="changeStyle(preset.ID)">
                <p>{{ $t(`generation.styles.${convertStyleKey(preset.style)}`) }}</p>
              </div>
            </div>
          </li>
        </ul>
      </template>
    </dropdown-menu>
  </div>
</template>
<script>

import DropdownMenu from "v-dropdown-menu/vue2"
import '@/assets/css/v-dropdown-menu.css'

export default {
  name: "StylePresetSelector",
  components: {
    DropdownMenu
  },
  computed: {
    availableStylePresets() {
      return this.$store.getters.availableEffectGenerationStylePresets || [];
    },
    selectedEffectGenerationStylePreset() {
      return this.availableStylePresets.find(preset => preset.ID === this.$store.getters.selectedEffectGenerationStylePresetID) || null;
    }
  },
  methods: {
    handleTouchStart(event) {
      event.preventDefault();
      const img = event.target;
      if (event.touches.length === 1) {
        const touch = event.touches[0];
        const imgRect = img.getBoundingClientRect();
        img.style.transform = `translate(0px, ${touch.clientY - imgRect.top - imgRect.height - 24}px) scale(2.4)`;
      } else {
        img.style.transform = 'scale(2.37037037)'; // 128px
      }
      img.style.transformOrigin = 'left bottom';
    },
    handleTouchEnd(event) {
      const img = event.target;
      img.style.transform = 'translate(0, 0) scale(1)';
      img.style.transformOrigin = 'left bottom';
    },
    disableContextMenu(event) {
      event.preventDefault();
    },
    changeStyle(styleID) {
      this.$store.dispatch('setSelectedEffectGenerationStylePresetID', styleID);
      this.closeDropdown();
    },
    closeDropdown() {
      this.$refs.dropdown?.hide();
    },
    convertStyleKey(style) {
      return style.replace(/-/g, '_').replace('3d', 'three_d');
    },
    scrollIntoViewIfNeeded() {
      this.$nextTick(() => {
        setTimeout(() => {
          const dropdown = this.$refs.dropdown.$el.querySelector(".v-dropdown-menu__container");
          if (dropdown) {
            const dropdownRect = dropdown.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            if (dropdownRect.bottom > viewportHeight) {
              const scrollDistance = dropdownRect.bottom - viewportHeight + 40;
              window.scrollBy({ top: scrollDistance, behavior: 'smooth' });
            }
          }
        }, 150);
      });
    }
  },
  mounted() {
    const images = this.$el.querySelectorAll('img');
    images.forEach(img => {
      img.addEventListener('touchstart', this.handleTouchStart);
      img.addEventListener('touchend', this.handleTouchEnd);
      img.addEventListener('contextmenu', this.disableContextMenu);
    });
  },
  beforeDestroy() {
    const images = this.$el.querySelectorAll('img');
    images.forEach(img => {
      img.removeEventListener('touchstart', this.handleTouchStart);
      img.removeEventListener('touchend', this.handleTouchEnd);
      img.removeEventListener('contextmenu', this.disableContextMenu);
    });
  },
};
</script>
<style lang="scss">
.dropdown-wrapper {
  margin-bottom: 10px;
}
.v-dropdown-menu {
  margin-left: 20px;
  $this: &;
  &.style-preset-selector & {
    &__trigger {
      button {
        margin-bottom: 0 !important;
        &:disabled {
          background-color: var(--inactive-color) !important;
          color: var(--background-color) !important;
        }
      }
    }
    &__container {
      position: absolute;
      border: 2px solid var(--foreground-color);
      border-radius: 0;
      box-shadow: 0 0 24px 0 rgba(0, 0, 0, 0.12);
      transform: translateY(-32px);
      min-width: calc(min(100vw - 40px, 300px));
      max-height: calc(min(100vh - 40px, 600px));
      overflow-y: auto;

      &::-webkit-scrollbar {
        width: 10px;
      }

      &::-webkit-scrollbar-thumb {
        background-color: var(--foreground-color);
        border: 1px solid var(--background-color);
      }

      &::-webkit-scrollbar-track {
        background: var(--background-color);
      }

      #{$this} {
        &__header {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 32px;
          padding: 5px;
          background-color: var(--foreground-color);
          color: var(--background-color);
        }
        &__body {
          ul {
            margin: 0;
            padding: 0;
            li {
              list-style: none;
              div {
                display: flex;
                padding: 0.3rem;
                color: var(--foreground-color);
                font-size: 16px;
                text-decoration: none;
                align-items: center;
                &:hover {
                  background-color: var(--inactive-color);
                  color: var(--background-color);
                }
                .preset-image-wrapper {
                  position: relative;
                  overflow: visible;
                  min-width: 64px !important;
                  min-height: 64px !important;
                  background-color: inherit;
                  img {
                    width: 54px;
                    height: 54px;
                    position: absolute;
                    border: 2px solid var(--foreground-color);
                    transition: transform 0.3s ease-in-out;
                    &:hover, &:active {
                      transform: scale(2.37037037); // 128px
                      transform-origin: left center;
                      z-index: 1000;
                    }
                  }
                }
                .preset-text-wrapper {
                  min-width: calc(100% - 64px);
                  min-height: 64px;
                  background-color: inherit;
                  color: inherit;
                  &:hover {
                    cursor: pointer;
                  }
                }
                &.selected {
                  background-color: var(--control-color);
                  color: var(--background-color);
                }
              }
            }
          }
        }
      }
    }
  }
  &.style-preset-selector {
    .zoom-enter-active,
    .zoom-leave-active {
      transition: all 0.6s cubic-bezier(0.075, 0.82, 0.165, 1);
    }

    .zoom-enter,
    .zoom-leave-to {
      transform: translateY(50px) scale(0.5);
      transform-origin: center top;
      opacity: 0;
    }
  }
}

.style-preset-selector-button {
  color: var(--foreground-color);
  background-color: var(--background-color) !important;
  border: 1px solid var(--foreground-color) !important;
  height: 32px;
  line-height: 18px;
  text-align: center;
  min-width: 222px;
  font-family: 'Graphik', sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px 13px;
  &:after {
    content: '';
    display: inline-block;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid var(--foreground-color);
    margin-right: 5px;
    right: 10px;
    position: absolute;
  }
}

@media (max-width: 470px) {
  .dropdown-wrapper {
    width: calc(100vw - 40px) !important;
  }
  .v-dropdown-menu {
    margin-left: 0;
    margin-top: 10px;
    width: 100%;
    &.style-preset-selector & {
      &__container {
        min-width: calc(100%) !important;
      }
    }
  }
  .dropdown-selector-text {
    display: block;
    margin-top: 20px;
  }
  .style-preset-selector-button {
    min-width: 100%;
  }
}
</style>
